/**
 * Таблица отчёта.
 *
 * Итоговая строка всегда показывает то, что посчитал сервер по выданным
 * строкам, а не сумму видимых на экране: при сортировке и усечении это
 * разные вещи, и подменять одно другим нельзя.
 */

import { useMemo, useState } from 'react'
import type { ReportColumn } from '../types'
import { formatCell, formatDelta, formatDeltaPoints, toNumber } from '../format'
import { Icon } from './Icon'
import { Empty, TableWrap } from './ui'

/**
 * Изменение к прошлому периоду.
 *
 * Направление несёт стрелка, а не только цвет: при дальтонизме зелёное
 * и красное неразличимы, и знак изменения нельзя оставлять на цвете одном.
 */
function DeltaCell({ value, points }: { value: number | null; points?: boolean }) {
  if (value === null) {
    return <span className="cell-muted" title="Нет базы для сравнения">—</span>
  }

  const tone = value > 0 ? 'delta--up' : value < 0 ? 'delta--down' : 'delta--flat'

  return (
    <span className={`delta ${tone}`}>
      <Icon name={value > 0 ? 'tri-up' : value < 0 ? 'tri-down' : 'dot'} size={10} />
      {points ? formatDeltaPoints(value) : formatDelta(value)}
    </span>
  )
}

interface Props {
  columns: ReportColumn[]
  rows: Record<string, unknown>[]
  totals?: Record<string, number | string | null>
  emptyText?: string
  /** Под текстом пустой таблицы: «Показать 12 месяцев по июнь». */
  emptyAction?: React.ReactNode
}

export function DataTable({ columns, rows, totals, emptyText = 'Нет строк', emptyAction }: Props) {
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)

  const sorted = useMemo(() => {
    if (!sort) return rows
    const column = columns.find((item) => item.key === sort.key)
    const numeric = column && column.type !== 'string' && column.type !== 'date'

    return [...rows].sort((a, b) => {
      const left = a[sort.key]
      const right = b[sort.key]

      if (numeric) {
        const l = toNumber(left)
        const r = toNumber(right)
        // Пустые значения всегда внизу — иначе они забивают верх таблицы.
        if (l === null && r === null) return 0
        if (l === null) return 1
        if (r === null) return -1
        return sort.direction === 'asc' ? l - r : r - l
      }

      const result = String(left ?? '').localeCompare(String(right ?? ''), 'ru')
      return sort.direction === 'asc' ? result : -result
    })
  }, [rows, sort, columns])

  if (rows.length === 0) {
    return <Empty icon="empty" title="Строк нет" hint={emptyText} action={emptyAction} />
  }

  const toggleSort = (key: string) => {
    setSort((current) =>
      current?.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'desc' },
    )
  }

  const isNumeric = (column: ReportColumn) => column.type !== 'string' && column.type !== 'date'

  return (
    <TableWrap>
      <table className="data">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`sortable${isNumeric(column) ? ' num' : ''}`}
                onClick={() => toggleSort(column.key)}
                title="Сортировать"
                aria-sort={sort?.key === column.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                {column.title}
                {sort?.key === column.key && (
                  <Icon name={sort.direction === 'asc' ? 'tri-up' : 'tri-down'} size={10} className="sort-arrow" />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, index) => (
            <tr key={index}>
              {columns.map((column) => {
                const value = row[column.key]
                const number = isNumeric(column) ? toNumber(value) : null

                if (column.type === 'delta' || column.type === 'delta_pp') {
                  return (
                    <td key={column.key} className="num">
                      <DeltaCell value={number} points={column.type === 'delta_pp'} />
                    </td>
                  )
                }

                return (
                  <td
                    key={column.key}
                    className={[
                      isNumeric(column) ? 'num' : '',
                      value === null || value === undefined ? 'cell-muted' : '',
                      number !== null && number < 0 ? 'cell-negative' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {formatCell(value, column.type)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
        {totals && (
          <tfoot>
            <tr>
              {columns.map((column, index) => (
                <td key={column.key} className={isNumeric(column) ? 'num' : undefined}>
                  {index === 0 ? (
                    'Итого'
                  ) : column.type === 'delta' || column.type === 'delta_pp' ? (
                    <DeltaCell value={toNumber(totals[column.key])} points={column.type === 'delta_pp'} />
                  ) : isNumeric(column) ? (
                    formatCell(totals[column.key], column.type)
                  ) : (
                    ''
                  )}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </TableWrap>
  )
}
